import { motion } from 'framer-motion';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Footer() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setSubmitMessage('올바른 이메일 주소를 입력해주세요.');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d6bgklaohb161tfd7hig', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitMessage('구독이 완료되었습니다!');
        setEmail('');
      } else {
        setSubmitMessage('구독 중 오류가 발생했습니다. 다시 시도해주세요.');
      }
    } catch (error) {
      setSubmitMessage('구독 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const footerLinks = {
    service: [
      { label: '채용정보', path: '/jobs' },
      { label: '인재찾기', path: '#' },
      { label: '커뮤니티', path: '/community' },
      { label: '마이페이지', path: '/mypage' },
      { label: '기업서비스', path: '#' },
    ],
    support: [
      { label: '공지사항', path: '#' },
      { label: 'FAQ', path: '#' },
      { label: '1:1 문의', path: '#' },
      { label: '이용가이드', path: '#' },
    ],
    company: [
      { label: '회사소개', path: '#' },
      { label: '이용약관', path: '#' },
      { label: '개인정보처리방침', path: '#' },
      { label: '제휴문의', path: '#' },
    ],
  };

  return (
    <footer className="relative bg-dark-deep pt-20 pb-8">
      <div className="container mx-auto px-20">
        <div className="grid grid-cols-5 gap-20 mb-20">
          {/* Left Section */}
          <div className="col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-5xl font-bold text-white leading-tight mb-8">
                온니잡과 함께<br />성장하세요
              </h3>

              <p className="text-base text-slate-400 mb-14">
                업계 최고의 구인구직 플랫폼에서 새로운 기회를 만나보세요
              </p>

              {/* Newsletter */}
              <form onSubmit={handleSubmit} data-readdy-form id="newsletter-form">
                <label className="block text-sm text-gray-400 mb-3">뉴스레터 구독</label>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="이메일 주소 입력"
                  className="w-full bg-transparent border-b border-white/20 text-white pb-3 mb-6 focus:outline-none focus:border-neon-purple transition-colors"
                  disabled={isSubmitting}
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="border border-white/20 text-white px-8 py-3 rounded-xl hover:border-neon-purple hover:text-neon-purple transition-all whitespace-nowrap cursor-pointer disabled:opacity-50"
                >
                  {isSubmitting ? '구독 중...' : '구독하기'}
                </button>
                {submitMessage && (
                  <p className={`mt-4 text-sm ${submitMessage.includes('완료') ? 'text-neon-green' : 'text-neon-pink'}`}>
                    {submitMessage}
                  </p>
                )}
              </form>
            </motion.div>
          </div>

          {/* Right Navigation */}
          <div className="col-span-3 grid grid-cols-3 gap-16">
            {/* Service */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h4 className="text-sm text-neon-purple font-bold mb-5">서비스</h4>
              <ul className="space-y-4">
                {footerLinks.service.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.path}
                      onClick={(e) => {
                        if (link.path !== '#') {
                          e.preventDefault();
                          navigate(link.path);
                        }
                      }}
                      className="text-base text-gray-400 hover:text-white transition-colors cursor-pointer"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Support */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h4 className="text-sm text-neon-purple font-bold mb-5">고객지원</h4>
              <ul className="space-y-4">
                {footerLinks.support.map((link) => (
                  <li key={link.label}>
                    <a href={link.path} className="text-base text-gray-400 hover:text-white transition-colors cursor-pointer">
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Company */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h4 className="text-sm text-neon-purple font-bold mb-5">회사</h4>
              <ul className="space-y-4">
                {footerLinks.company.map((link) => (
                  <li key={link.label}>
                    <a href={link.path} className="text-base text-gray-400 hover:text-white transition-colors cursor-pointer">
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-white/10 pt-8 flex items-center justify-between">
          <p className="text-xs text-slate-600">&copy; 2025 온니잡. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
